import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams, useLocation } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { buildTicketsPath } from "../../utils/ticketNavigation";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
		padding: theme.spacing(1.5, 2),
		height: 84, // Fixed height for standard chat list feel
		transition: "background-color 0.2s",
		"&:hover": {
			backgroundColor: theme.palette.type === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.04)",
		},
	},
	pendingTicket: {
		cursor: "unset",
	},
	ticketQueueColor: {
		position: "absolute",
		top: 0,
		bottom: 0,
		left: 0,
		width: 4,
	},
	avatar: {
		width: 48,
		height: 48,
		marginRight: theme.spacing(2),
	},
	rowContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
	},
	nameContainer: {
		display: "flex",
		alignItems: "center",
		overflow: "hidden",
		flex: 1,
	},
	contactName: {
		fontWeight: 600,
		fontSize: "0.95rem",
		color: theme.palette.text.primary,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	closedBadge: {
		fontSize: "0.65rem",
		fontWeight: 700,
		padding: "2px 6px",
		borderRadius: 4,
		backgroundColor: theme.palette.type === "light" ? "#F1F5F9" : "#334155",
		color: theme.palette.type === "light" ? "#475569" : "#94A3B8",
		marginLeft: 8,
		whiteSpace: "nowrap",
	},
	lastMessageTime: {
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
		marginLeft: 8,
		whiteSpace: "nowrap",
		flexShrink: 0,
	},
	messageContainer: {
		display: "flex",
		alignItems: "center",
		overflow: "hidden",
		flex: 1,
		marginTop: 4,
	},
	userTag: {
		fontSize: "0.65rem",
		fontWeight: 700,
		padding: "2px 6px",
		borderRadius: 4,
		backgroundColor: theme.palette.type === "light" ? "#EEF2FF" : "#312E81",
		color: theme.palette.type === "light" ? "#4F46E5" : "#A5B4FC",
		border: `1px solid ${theme.palette.type === "light" ? "#C7D2FE" : "#4338CA"}`,
		marginRight: 8,
		whiteSpace: "nowrap",
		flexShrink: 0,
	},
	contactLastMessage: {
		fontSize: "0.85rem",
		color: theme.palette.text.secondary,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	badgeContainer: {
		marginLeft: 8,
		flexShrink: 0,
		display: "flex",
		alignItems: "center",
	},
	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},
	acceptButton: {
		marginLeft: 8,
		flexShrink: 0,
	},
}));

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const location = useLocation();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(
			buildTicketsPath({
				ticketId: id,
				whatsappId: ticket.whatsappId,
				search: location.search,
			})
		);
	};

	const handleSelectTicket = id => {
		history.push(
			buildTicketsPath({
				ticketId: id,
				whatsappId: ticket.whatsappId,
				search: location.search,
			})
		);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={e => {
					if (ticket.status === "pending") return;
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
				alignItems="flex-start"
			>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Nessuna coda"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					/>
				</Tooltip>
				<ListItemAvatar>
					<Avatar className={classes.avatar} src={ticket?.contact?.profilePicUrl} />
				</ListItemAvatar>
				
				<ListItemText
					disableTypography
					primary={
						<div className={classes.rowContainer}>
							<div className={classes.nameContainer}>
								<Typography className={classes.contactName}>
									{ticket.contact.name}
								</Typography>
								{ticket.status === "closed" && (
									<span className={classes.closedBadge}>
										Closed
									</span>
								)}
							</div>
							{ticket.lastMessage && (
								<Typography className={classes.lastMessageTime}>
									{isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
										<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
									) : (
										<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
									)}
								</Typography>
							)}
						</div>
					}
					secondary={
						<div className={classes.rowContainer}>
							<div className={classes.messageContainer}>
								{ticket.whatsappId && (
									<span className={classes.userTag} title={i18n.t("ticketsList.connectionTitle")}>
										{ticket.whatsapp?.name}
									</span>
								)}
								<Typography className={classes.contactLastMessage}>
									{ticket.lastMessage ? (
										<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
									) : (
										<span>...</span>
									)}
								</Typography>
							</div>

							<div className={classes.badgeContainer}>
								{ticket.status === "pending" ? (
									<ButtonWithSpinner
										color="primary"
										variant="contained"
										className={classes.acceptButton}
										size="small"
										loading={loading}
										onClick={e => handleAcepptTicket(ticket.id)}
									>
										{i18n.t("ticketsList.buttons.accept")}
									</ButtonWithSpinner>
								) : (
									<Badge
										overlap="rectangular"
										badgeContent={ticket.unreadMessages}
										classes={{ badge: classes.badgeStyle }}
									/>
								)}
							</div>
						</div>
					}
				/>
			</ListItem>
			<Divider component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
